// src/hooks/useDateFilter.js
import { useState, useCallback } from "react";

const useDateFilter = (initialFilter = {}) => {
  const [dateFilter, setDateFilter] = useState({
    viewType: "monthly",
    selectedYear: new Date().getFullYear(),
    selectedQuarter: 1,
    selectedMonth: new Date().getMonth() + 1,
    ...initialFilter,
  });

  // Helper function to get last day of month
  const getLastDayOfMonth = (year, month) => {
    return new Date(year, month, 0).getDate();
  };

  // Helper function to format date as YYYY-MM-DD (for display only)
  const formatDate = (year, month, day) => {
    return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
  };

  // NEW: Convert local date to UTC ISO string with proper time boundaries
  const toUTCISOString = (year, month, day, isEndOfDay = false) => {
    // Create date in local timezone
    const localDate = new Date(year, month - 1, day);

    if (isEndOfDay) {
      // Set to end of day (23:59:59.999) in local time, then convert to UTC
      localDate.setHours(23, 59, 59, 999);
    } else {
      // Set to start of day (00:00:00.000) in local time, then convert to UTC
      localDate.setHours(0, 0, 0, 0);
    }

    // Convert to UTC ISO string (includes time and timezone info)
    return localDate.toISOString();
  };

  // Get date range based on filter - RETURNS UTC ISO STRINGS FOR API
  const getDateRange = useCallback(() => {
    const { viewType, selectedYear, selectedQuarter, selectedMonth } =
      dateFilter;

    switch (viewType) {
      case "all":
        return {
          from_date: null,
          to_date: null,
          label: "All Time",
        };

      case "yearly":
        return {
          from_date: toUTCISOString(selectedYear, 1, 1, false), // Jan 1, 00:00:00 local -> UTC
          to_date: toUTCISOString(selectedYear, 12, 31, true), // Dec 31, 23:59:59 local -> UTC
          label: `Year ${selectedYear}`,
        };

      case "quarterly": {
        const quarterStartMonth = (selectedQuarter - 1) * 3 + 1;
        const quarterEndMonth = quarterStartMonth + 2;
        const lastDayOfQuarter = getLastDayOfMonth(
          selectedYear,
          quarterEndMonth,
        );
        return {
          from_date: toUTCISOString(selectedYear, quarterStartMonth, 1, false),
          to_date: toUTCISOString(
            selectedYear,
            quarterEndMonth,
            lastDayOfQuarter,
            true,
          ),
          label: `Q${selectedQuarter} ${selectedYear}`,
        };
      }

      case "monthly": {
        const lastDayOfMonth = getLastDayOfMonth(selectedYear, selectedMonth);

        return {
          from_date: toUTCISOString(selectedYear, selectedMonth, 1, false),
          to_date: toUTCISOString(
            selectedYear,
            selectedMonth,
            lastDayOfMonth,
            true,
          ),
          label: `${new Date(2000, selectedMonth - 1, 1).toLocaleString("default", { month: "long" })} ${selectedYear}`,
        };
      }

      default:
        return {
          from_date: toUTCISOString(selectedYear, 1, 1, false),
          to_date: toUTCISOString(selectedYear, 12, 31, true),
          label: `Year ${selectedYear}`,
        };
    }
  }, [dateFilter]);

  // Optional: Get just the date strings (YYYY-MM-DD) for display purposes
  const getDateRangeAsDateStrings = useCallback(() => {
    const range = getDateRange();
    if (!range.from_date || !range.to_date) return range;

    // Extract just the date part from ISO strings for display
    return {
      from_date: range.from_date.split("T")[0],
      to_date: range.to_date.split("T")[0],
      label: range.label,
    };
  }, [getDateRange]);

  const updateFilter = useCallback((updates) => {
    setDateFilter((prev) => ({
      ...prev,
      ...updates,
    }));
  }, []);

  const resetFilter = useCallback(() => {
    setDateFilter({
      viewType: "yearly",
      selectedYear: new Date().getFullYear(),
      selectedQuarter: 1,
      selectedMonth: new Date().getMonth() + 1,
    });
  }, []);

  return {
    dateFilter,
    updateFilter,
    resetFilter,
    getDateRange, // Returns UTC ISO strings for API calls
    getDateRangeAsDateStrings, // Returns just YYYY-MM-DD for display
  };
};

export default useDateFilter;
